import React, { useState, useEffect, useContext } from "react";
import useToastContext from "hooks/useToastContext";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { apiPut, apiGet, apiPost } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import { useTranslation } from "react-i18next";
import OInputField from "components/reusable/OInputField";
import formValidation from "utils/formValidation";
import AuthContext from "context/AuthContext";
import ErrorMessage from "components/ErrorMessage";
import OImage from "components/reusable/OImage";
import { Editor } from "@tinymce/tinymce-react";
import noImageFound from "../../assets/images/No-image-found.jpg";
import Select from 'react-select';

const BlogEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [content, setContent] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [postDate, setPostDate] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
  });
  const notification = useToastContext();
  const [photo, setPhoto] = useState(null);
  const storedImage = location?.state?.blogImage;
  const [photoError] = useState("");

  const { updatePageName } = useContext(AuthContext);

  useEffect(() => {
    fetchCategories();
    if (location?.state) {
      reset({
        title: location.state.title,
        writer_name: location.state.writer_name,
        metaTitle: location.state.metaTitle,
        metaDescription: location.state.metaDescription,
        keywords: location.state.keywords,
      });

      if (location.state.description) {
        setContent(location.state.description);
      }

      if (location.state.categories) {
        const preselected = location.state.categories.map((cat) => ({
          value: cat._id,
          label: cat.name,
        }));
        setSelectedCategories(preselected);
      }


      // Set post date
      if (location.state.post_date) {
        const date = new Date(location.state.post_date).toISOString().split('T')[0];
        setPostDate(date);
      } else {
        setPostDate(new Date().toISOString().split('T')[0]);
      }
    }
  }, [location, reset]);

  const fetchCategories = async () => {
    try {
      const result = await apiGet(apiPath.blogCategory, { page: 1, pageSize: 100 });
      if (result?.data?.success) {
        const formatted = result.data.results.docs.map((category) => ({
          value: category._id,
          label: category.name,
        }));
        setCategories(formatted);
      }
    } catch (error) {
      console.log("Error fetching categories:", error);
    }
  };

  // Image upload handler for TinyMCE
  const handleImageUpload = (blobInfo, progress) => {
    return new Promise(async (resolve, reject) => {
      try {
        setUploadingImage(true);
        
        const formData = new FormData();
        formData.append('image', blobInfo.blob(), blobInfo.filename());

        console.log('Uploading image:', blobInfo.filename());

        const response = await apiPost(apiPath.uploadImage, formData);
        
        console.log('Upload response:', response);

        if (response?.data?.success) {
          const imageUrl = response.data.results?.imageUrl;
          if (imageUrl) {
            console.log('Image uploaded successfully. URL:', imageUrl);
            resolve(imageUrl);
          } else {
            reject('Upload successful but no image URL in response');
          }
        } else {
          const errorMsg = response?.data?.message || 'Upload failed on server';
          console.error('Server rejected upload:', errorMsg);
          reject(errorMsg);
        }
      } catch (error) {
        console.error('Image upload catch error:', error);
        const errorMsg = error.response?.data?.message || error.message || 'Upload failed';
        reject('Image upload failed: ' + errorMsg);
      } finally {
        setUploadingImage(false);
      }
    });
  };

  const onSubmit = async (data) => {
    try {
      if (content && content !== "<p><br></p>") {
        const formData = new FormData();
        formData.append("title", data?.title?.trim());
        formData.append("writer_name", data?.writer_name);
        formData.append("description", content);
        formData.append("metaTitle", data?.metaTitle);
        formData.append("metaDescription", data?.metaDescription);
        formData.append("keywords", data?.keywords);
        formData.append("post_date", postDate ? new Date(postDate).toISOString() : new Date().toISOString());
        
        selectedCategories.forEach((category) => {
          formData.append("categories", category.value);
        });

        
        if (photo) {
          formData.append("blogImage", photo);
        }
        
        console.log('Updating blog with form data...');
        
        const path = apiPath.blog + "/" + location?.state?._id;
        const result = await apiPut(path, formData);
        if (result.data.success) {
          navigate("/blog-management");
          notification.success(result?.data.message);
        } else {
          notification.error(result?.data.message);
        }
      } else {
        notification.error("Please enter description");
      }
    } catch (error) {
      console.log("error in edit blog==>>>>", error);
      notification.error(error.message);
    }
  };

  useEffect(() => {
    updatePageName(t("EDIT_BLOG"));
  }, []);

  const handleCategoryChange = (e) => {
    const options = e.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setSelectedCategories(selected);
  };

  const getImageSrc = () => {
    if (photo) {
      return typeof photo === "object" ? URL.createObjectURL(photo) : photo;
    }
    return storedImage || noImageFound;
  };

  useEffect(() => {
    return () => {
      if (photo && typeof photo === "object") {
        URL.revokeObjectURL(photo);
      }
    };
  }, [photo]);

  return (
    <>
      <div>
        <div className="bg-[#F9F9F9]">
          <div className="px-3 py-4">
            <div className="bg-white border border-[#E9EDF9] rounded-lg">
              <div className=" border-b-[#E3E3E3] grid gap-2 px-4 2xl:grid-cols-3 xl:grid-cols-2 lg:grid lg:grid-cols-1">
                <div className="col-span-3 flex flex-wrap  items-center" />
              </div>
              
              <div className="flex   pl-5 pt-4">
                <strong>{t("O_ADMIN_BLOG_IMAGE_SIZE_SUGGESTION")}</strong>
              </div>
              
              <div className="pl-5 pt-4 flex items-center gap-4">
                <div>
                  <label className="mb-2 block pr-1">
                    <strong>{t("UPLOAD_IMAGE")}</strong>
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/png,image/jpeg,image/jpg"
                    name="photo"
                    onChange={(event) => {
                      if (event.target.files?.[0]) {
                        setPhoto(event.target.files[0]);
                      }
                    }}
                  />
                </div>
                
                {photo && (
                  <button
                    type="button"
                    onClick={() => {
                      setPhoto(null);
                      document.querySelector('input[name="photo"]').value = "";
                    }}
                    className="text-black bg-[#E1E1E1] font-normal px-4 py-2 text-sm outline-none focus:outline-none rounded ease-linear transition-all duration-150 hover:bg-gray-300 mt-6"
                  >
                    {t("CANCEL")}
                  </button>
                )}
              </div>
              
              {photoError && <ErrorMessage message={photoError} />}
              
              <div className="pl-5 pt-4" style={{width:"300px"}}>
                <OImage src={getImageSrc()} fallbackUrl={noImageFound} />
              </div>

              <div className="p-5 shadow-md">
                <OInputField
                  inputLabel={
                    <>
                      {t("TITLE")}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  wrapperClassName="relative z-0 mb-6 w-full group"
                  name="title"
                  type="text"
                  autoFocus
                  register={register("title", formValidation["title"])}
                  errors={errors}
                />
                
                <OInputField
                  wrapperClassName="relative z-0 mb-6 w-full group"
                  inputLabel={<>{t("WRITER_NAME")}</>}
                  type="text"
                  name="writer_name"
                  register={register("writer_name")}
                />

                {/* Post Date Field */}
                <div className='relative z-0 mb-6 w-full group'>
                  <label className='block mb-2 text-sm font-medium text-gray-900'>
                    {t('POST_DATE')} <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='date'
                    value={postDate}
                    onChange={(e) => setPostDate(e.target.value)}
                    className='bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5'
                    required
                  />
                </div>

                <div className='relative z-10 mb-6 w-full group'>
                  <label className='block mb-2 text-sm font-medium text-gray-900'>
                    {t('CATEGORIES')}
                  </label>
                  <Select
                    isMulti
                    name="categories"
                    options={categories}
                    value={selectedCategories}
                    onChange={(selectedOptions) => setSelectedCategories(selectedOptions || [])}
                    className="basic-multi-select"
                    classNamePrefix="select"
                  />
                </div>

                <OInputField
                  wrapperClassName="relative z-0 mb-6 w-full group"
                  type="text"
                  name="metaTitle"
                  inputLabel={<>{t("META_TITLE")}</>}
                  register={register("metaTitle", formValidation["metaTitle"])}
                />
                
                <OInputField
                  wrapperClassName="relative z-0 mb-6 w-full group"
                  type="text"
                  name="metaDescription"
                  inputLabel={<>{t("META_DESCRIPTION")}</>}
                  register={register(
                    "metaDescription",
                    formValidation["metaDescription"]
                  )}
                />
                
                <OInputField
                  wrapperClassName="relative z-0 mb-6 w-full group"
                  type="text"
                  name="keywords"
                  inputLabel={<>{t("KEYWORDS")}</>}
                  register={register("keywords", formValidation["keywords"])}
                />
                
                <div
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                    padding: "10px",
                  }}
                >
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Blog Content {uploadingImage && <span className="text-blue-500 text-sm">(Uploading image...)</span>}
                    </label>
                  </div>
                  
                  <Editor
                    apiKey="mkzldhxz903im4nozx302n82dfa451insn0l8g423ay9x8s0"
                    value={content}
                    onEditorChange={(newValue) => setContent(newValue)}
                    init={{
                      height: 500,
                      menubar: "file edit view insert format tools help",
                      plugins: [
                        "advlist",
                        "autolink",
                        "lists",
                        "link",
                        "image",
                        "charmap",
                        "preview",
                        "anchor",
                        "searchreplace",
                        "visualblocks",
                        "code",
                        "fullscreen",
                        "insertdatetime",
                        "media",
                        "table",
                        "paste",
                        "help",
                        "wordcount",
                      ],
                      toolbar: 'undo redo | formatselect | bold italic backcolor | ' +
                        'alignleft aligncenter alignright alignjustify | ' +
                        'link image | bullist numlist outdent indent | removeformat | code | help',
                      content_style: "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                      
                      // Image upload configuration
                      images_upload_handler: handleImageUpload,
                      automatic_uploads: true,
                      paste_data_images: false,
                      
                      // File picker configuration
                      file_picker_types: 'image',
                      file_picker_callback: (callback, value, meta) => {
                        const input = document.createElement('input');
                        input.setAttribute('type', 'file');
                        input.setAttribute('accept', 'image/*');
                        
                        input.onchange = function() {
                          const file = this.files[0];
                          if (file) {
                            const blobInfo = {
                              blob: () => file,
                              filename: () => file.name
                            };
                            
                            handleImageUpload(blobInfo, () => {})
                              .then(imageUrl => {
                                callback(imageUrl, { alt: file.name });
                              })
                              .catch(error => {
                                console.error('File picker upload failed:', error);
                                notification.error('Failed to upload image');
                              });
                          }
                        };
                        
                        input.click();
                      },
                      
                      // Prevent direct pasting of images
                      paste_preprocess: (plugin, args) => {
                        const html = args.content;
                        if (html.includes('<img')) {
                          notification.error('Please use the image upload button to add images instead of pasting.');
                          args.content = '';
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-end p-6 border-t border-solid border-slate-200 rounded-b">
        <button
          className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6  ease-linear transition-all duration-150"
          type="button"
          onClick={() => navigate("/blog-management")}
        >
          {t("O_BACK")}
        </button>

        <button
          className="bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1  ease-linear transition-all duration-150"
          type="submit"
          onClick={handleSubmit(onSubmit)}
        >
          {t("O_UPDATE")}
        </button>
      </div>
    </>
  );
};

export default BlogEdit;